import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BellOff, Grid3x3, Mic, MicOff, Phone, PhoneForwarded, PhoneOff, Volume2 } from 'lucide-react';

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];

const initials = (name) => (name || '').split(/\s+/).filter(Boolean).slice(0, 2)
  .map((w) => w[0].toLocaleUpperCase('tr')).join('');

function RoundButton({ label, active, onClick, children, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      aria-pressed={active}
      className="flex flex-col items-center gap-2 disabled:opacity-40"
    >
      <span className={`flex h-16 w-16 items-center justify-center rounded-full transition ${
        active ? 'bg-white text-slate-900' : 'bg-white/15 text-white active:bg-white/30'}`}
      >
        {children}
      </span>
      <span className="text-xs text-white/80">{label}</span>
    </button>
  );
}

/**
 * Full-screen call screen for phones (the app, and narrow browser windows):
 * caller up top, round controls, big answer / hang-up buttons — the layout
 * people know from their phone / WhatsApp. Same actions as the desktop card.
 */
export default function CallScreen({ sp, call, elapsed }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [panel, setPanel] = useState(null); // null | 'keypad' | 'transfer'
  const [digits, setDigits] = useState('');
  const [transferTo, setTransferTo] = useState('');
  const ringing = call.state === 'ringing';
  const active = call.state === 'active';
  const canTransfer = active;
  const name = call.name || t('shell.softphone.unknownNumber');
  const status = ringing ? t('shell.softphone.incomingCall')
    : call.state === 'calling' ? t('shell.softphone.calling')
      : `${t('shell.softphone.inCall')}${elapsed ? ` · ${elapsed}` : ''}`;
  const directory = sp.directory || [];

  const press = (k) => {
    setDigits((d) => (d + k).slice(-24));
    sp.sendDigit(k);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={status}
      className="fixed inset-0 z-[60] flex flex-col bg-gradient-to-b from-slate-900 via-slate-900 to-slate-800 text-white"
      style={{ paddingTop: 'max(env(safe-area-inset-top), 1.5rem)', paddingBottom: 'max(env(safe-area-inset-bottom), 1.5rem)' }}
    >
      {/* caller */}
      <div className="flex flex-col items-center px-6 pt-8 text-center">
        <div className={`text-sm text-emerald-300 ${ringing ? 'animate-pulse' : ''}`}>{status}</div>
        <div className="mt-6 flex h-28 w-28 items-center justify-center rounded-full bg-emerald-600 text-4xl font-semibold shadow-lg">
          {initials(call.name) || <Phone size={44} />}
        </div>
        <div className="mt-5 max-w-full truncate text-2xl font-semibold">{name}</div>
        {call.number && <div className="mt-1 text-base text-white/70">{call.number}</div>}
        {call.partyUrl && (
          <button type="button" onClick={() => navigate(call.partyUrl)} className="mt-2 text-sm text-emerald-300 underline-offset-2 hover:underline">
            {t('shell.softphone.openRecord')} →
          </button>
        )}
      </div>

      <div className="flex-1" />

      {/* keypad / transfer panels */}
      {panel === 'keypad' && (
        <div className="mx-auto w-full max-w-xs px-6">
          <div className="mb-3 h-8 truncate text-center text-2xl tracking-widest">{digits}</div>
          <div className="grid grid-cols-3 gap-3">
            {KEYS.map((k) => (
              <button key={k} type="button" onClick={() => press(k)}
                className="h-14 rounded-full bg-white/15 text-2xl active:bg-white/30">
                {k}
              </button>
            ))}
          </div>
        </div>
      )}
      {panel === 'transfer' && canTransfer && (
        <form
          className="mx-auto w-full max-w-xs space-y-3 px-6"
          onSubmit={(e) => {
            e.preventDefault();
            sp.transfer(transferTo).then(() => setPanel(null)).catch(() => {});
          }}
        >
          <div className="text-center text-sm font-semibold">{t('shell.softphone.transferCall')}</div>
          {directory.length > 0 && (
            <div className="max-h-40 space-y-1 overflow-y-auto">
              {directory.map((d) => (
                <button key={d.extension} type="button" onClick={() => setTransferTo(d.extension)}
                  className={`w-full rounded-lg px-3 py-2 text-left text-sm ${transferTo === d.extension ? 'bg-emerald-600' : 'bg-white/10'}`}>
                  {d.name} <span className="text-white/60">({d.extension})</span>
                </button>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <input
              value={transferTo}
              onChange={(e) => setTransferTo(e.target.value)}
              placeholder={t('shell.softphone.extensionOrNumber')}
              inputMode="tel"
              aria-label={t('shell.softphone.transferTarget')}
              className="min-w-0 flex-1 rounded-lg bg-white/10 px-3 py-2 text-sm placeholder-white/50"
            />
            <button type="submit" disabled={!transferTo || call.transferring}
              className="rounded-lg bg-emerald-600 px-3 py-2 text-sm disabled:opacity-50">
              {call.transferring ? t('shell.softphone.transferring') : t('shell.softphone.transfer')}
            </button>
          </div>
        </form>
      )}

      {/* controls */}
      {ringing ? (
        <div className="px-10 pb-6">
          {!call.silenced && (
            <div className="mb-8 flex justify-center">
              <button type="button" onClick={sp.silenceRing} title={t('shell.softphone.silenceHint')}
                className="flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm">
                <BellOff size={16} /> {t('shell.softphone.silence')}
              </button>
            </div>
          )}
          <div className="flex items-start justify-between">
            <button type="button" onClick={sp.hangup} aria-label={t('shell.softphone.reject')} className="flex flex-col items-center gap-2">
              <span className="flex h-[72px] w-[72px] items-center justify-center rounded-full bg-red-600 shadow-lg active:bg-red-700">
                <PhoneOff size={30} />
              </span>
              <span className="text-sm">{t('shell.softphone.reject')}</span>
            </button>
            <button type="button" onClick={sp.answer} aria-label={t('shell.softphone.answer')} className="flex flex-col items-center gap-2">
              <span className="flex h-[72px] w-[72px] animate-pulse items-center justify-center rounded-full bg-emerald-500 shadow-lg active:bg-emerald-600">
                <Phone size={30} />
              </span>
              <span className="text-sm">{t('shell.softphone.answer')}</span>
            </button>
          </div>
        </div>
      ) : (
        <div className="px-8 pb-6 pt-6">
          <div className="mx-auto grid max-w-xs grid-cols-3 gap-y-6">
            <RoundButton label={call.muted ? t('shell.softphone.unmute') : t('shell.softphone.mute')}
              active={call.muted} onClick={sp.toggleMute}>
              {call.muted ? <MicOff size={26} /> : <Mic size={26} />}
            </RoundButton>
            <RoundButton label={t('shell.softphone.keypad')} active={panel === 'keypad'}
              onClick={() => setPanel((p) => (p === 'keypad' ? null : 'keypad'))}>
              <Grid3x3 size={26} />
            </RoundButton>
            {sp.canUseSpeaker ? (
              <RoundButton label={t('shell.softphone.speaker')} active={!!call.speaker} onClick={sp.toggleSpeaker}>
                <Volume2 size={26} />
              </RoundButton>
            ) : (
              <RoundButton label={t('shell.softphone.transferCall')} active={panel === 'transfer'} disabled={!canTransfer}
                onClick={() => setPanel((p) => (p === 'transfer' ? null : 'transfer'))}>
                <PhoneForwarded size={26} />
              </RoundButton>
            )}
            {sp.canUseSpeaker && (
              <RoundButton label={t('shell.softphone.transferCall')} active={panel === 'transfer'} disabled={!canTransfer}
                onClick={() => setPanel((p) => (p === 'transfer' ? null : 'transfer'))}>
                <PhoneForwarded size={26} />
              </RoundButton>
            )}
          </div>
          <div className="mt-8 flex justify-center">
            <button type="button" onClick={sp.hangup} aria-label={t('shell.softphone.hangUp')}
              className="flex h-[72px] w-[72px] items-center justify-center rounded-full bg-red-600 shadow-lg active:bg-red-700">
              <PhoneOff size={30} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
